import React, { useState, useEffect } from "react";
import { ArrowUpRight, Music, PawPrint } from "lucide-react";

// Curated warm rescue/companion photos. Add or swap with EPAWA's photos later.
const ANIMAL_PHOTOS = [
  {
    src: "https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=900&q=85",
    alt: "A dog resting peacefully",
  },
  {
    src: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=900&q=85",
    alt: "An orange tabby cat curled by a window",
  },
  {
    src: "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=900&q=85",
    alt: "A golden retriever puppy",
  },
  {
    src: "https://images.unsplash.com/photo-1535268647677-300dbf3d78d1?w=900&q=85",
    alt: "A sleepy kitten",
  },
  {
    src: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=900&q=85",
    alt: "Two horses in golden light",
  },
];

const ROTATE_MS = 6000;

export default function PawsHarmonyCard() {
  const [idx, setIdx] = useState(0);

  // Preload all images so crossfade is instant
  useEffect(() => {
    ANIMAL_PHOTOS.forEach((p) => {
      const img = new Image();
      img.src = p.src;
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % ANIMAL_PHOTOS.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-32" data-testid="paws-harmony-card">
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#2A4A3A] via-[var(--ink-forest)] to-[#1a2820] text-[var(--bg-cream)] p-10 lg:p-14">
        {/* Decorative orbs */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[var(--gold)]/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[var(--terracotta)]/10 blur-3xl" />

        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--gold)]/15 text-[var(--gold)] text-xs font-semibold mb-5">
              <PawPrint className="w-3.5 h-3.5" /> A sister sanctuary
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bg-cream)] tracking-tight leading-tight">
              Comfort for the
              <span className="italic text-[var(--gold)]"> four-legged</span> ones too.
            </h2>
            <p className="mt-5 text-[var(--bg-cream)]/80 leading-relaxed text-base max-w-xl">
              Visit our sister site, <strong>Paws & Harmony</strong> — a songbook of original music,
              cinematic illustrations, and stories made for pets and the humans who love them.
              Songs about love, adoption, fostering, sobriety. Made for rescues, shelters, and
              quiet evenings on the couch.
            </p>
            <a
              href="https://profile-sync-error.emergent.host"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta)] hover:text-white transition-colors"
              data-testid="home-paws-harmony-link"
            >
              Visit Paws &amp; Harmony <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm">
              <div className="aspect-square rounded-3xl bg-[var(--bg-cream)]/5 border border-[var(--bg-cream)]/15 overflow-hidden relative">
                {ANIMAL_PHOTOS.map((p, i) => (
                  <img
                    key={p.src}
                    src={p.src}
                    alt={p.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                      i === idx ? "opacity-90" : "opacity-0"
                    }`}
                    data-testid={`paws-animal-${i}`}
                  />
                ))}
                {/* Tiny progress dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {ANIMAL_PHOTOS.map((_, i) => (
                    <span
                      key={i}
                      className={`block w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        i === idx ? "bg-[var(--gold)] w-4" : "bg-[var(--bg-cream)]/40"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-2xl bg-[var(--gold)] flex items-center justify-center shadow-2xl">
                <Music className="w-9 h-9 text-[var(--ink-forest)]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
